import { useTheme } from "@mui/material";
import { Axis } from "@visx/axis";
import { Line as VLine } from "@visx/shape";
import { Fragment } from "react";
import type { ScaleLinear } from "types/charts";
import type { NumberValue } from "@visx/vendor/d3-scale";
import useAreaChart from "../AreaChartProvider/useAreaChartStore";
import useYAxis from "../YAxisProvider/useYAxisStore";
import useYAxisActions from "../YAxisProvider/useYAxisActions";

interface GridLinesProps {
  scale: ScaleLinear;
  tickValues?: NumberValue[];
  hideGridLines?: boolean;
}

function GridLines(props: GridLinesProps) {
  const { scale, tickValues, hideGridLines } = props;

  const theme = useTheme();
  const { margin, width } = useAreaChart();

  if (hideGridLines) return null;

  return (
    <>
      {tickValues?.map((value, i) => (
        <VLine
          key={i}
          from={{ x: margin.left, y: scale(value) }}
          to={{ x: width - margin.right, y: scale(value) }}
          stroke={theme.palette.grey[60]}
          strokeDasharray="3,4"
          strokeWidth={1}
        />
      ))}
    </>
  );
}

function YAxis() {
  const theme = useTheme();
  const { margin, width } = useAreaChart();
  const { yAxis } = useYAxis();
  const { getYScale } = useYAxisActions();

  return (
    <>
      {yAxis.map((axis, index) => {
        const scale = getYScale(axis.axisId);
        if (!scale) return null;
        const left = axis.orientation === "left" ? margin.left : width - margin.right;

        return (
          <Fragment key={index}>
            <GridLines scale={scale} tickValues={axis.tickValues} hideGridLines={axis.hideGridLines} />
            <Axis
              orientation={axis.orientation}
              left={left}
              scale={scale || 0}
              stroke={theme.palette.grey[20]}
              tickLabelProps={{
                fill: theme.palette.grey[100],
                fontSize: 12,
                textAnchor: axis.orientation === "left" ? "end" : "start",
                alignmentBaseline: "middle",
                dominantBaseline: "middle",
                ...axis.tickLabelProps,
              }}
              {...axis}
              numTicks={axis.numTicks}
              hideTicks={axis.disableTicks}
              hideZero={axis.hideZero}
              hideAxisLine
              tickFormat={(v) => axis.tickFormat?.(v as number)}
            />
          </Fragment>
        );
      })}
    </>
  );
}

export default YAxis;
